/**
 * Libellés chiffrés de la mémoire de conversation (rond du composer, toast de
 * compaction). Une seule source pour que le popover et le toast parlent la même
 * langue : « 72 % » d'un côté et « 72,4 % » de l'autre feraient douter l'avocat
 * que les deux chiffres désignent la même mémoire.
 *
 * Seulement des pourcentages : un volume en « tokens » est un terme technique
 * anglais, sans intérêt pour l'avocat.
 *
 * Typographie française : virgule décimale et espace insécable avant « % », pour
 * que l'unité ne passe jamais seule à la ligne dans un popover étroit.
 */

const NO_BREAK_SPACE = '\u00a0';

const percentFormatter = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

/** `72.4` → `« 72,4 % »` ; borné à [0, 100] comme la jauge qui l'affiche. */
export function formatMemoryPercent(percentUsed: number): string {
  const bounded = Math.min(100, Math.max(0, percentUsed));
  return `${percentFormatter.format(bounded)}${NO_BREAK_SPACE}%`;
}

/**
 * Part de la mémoire qu'occupent `tokens`, rapportée au seuil de compaction
 * automatique (100 % = compaction), ou `null` sans seuil exploitable.
 *
 * `compactionThreshold` d'abord, `tokensMax` ensuite : le contrat les rend égaux,
 * mais c'est le seuil qui définit la jauge. Jamais la fenêtre du modèle
 * (`contextWindow`), qui donnerait un pourcentage que le rond ne montre pas.
 */
export function memoryPercentOf(
  tokens: number,
  gauge: { compactionThreshold?: number; tokensMax?: number },
): number | null {
  const threshold = [gauge.compactionThreshold, gauge.tokensMax]
    .find((value): value is number => value !== undefined && Number.isFinite(value) && value > 0);
  if (threshold === undefined || !Number.isFinite(tokens)) return null;
  return (Math.max(0, tokens) / threshold) * 100;
}
